// src/components/ModuleDashboard.jsx
import React, { useEffect, useState } from "react";
import { RefreshCw, CalendarDays } from "lucide-react";
import axios from "axios";
import { motion } from "framer-motion";
import ModeDropdown from "./ModeDropdown";
import { useTheme } from "../contexts/ThemeContext";

export default function ModuleDashboard({
  moduleKey = "WS",
  eyebrow = "Web Platform",
  title = "Web Surrogate Dashboard",
  subtitle = "Overview of Approved, Rejected, and Processing cases",
  sectionTitle = "Web Surrogates",
  sectionSubtitle = "Live data from the Web Surrogates system",
  fetchUrl,
  totalLabel = "cases",
  showWorkingBadge = false,
}) {
  const [data, setData] = useState(null);
  const [totalCases, setTotalCases] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [filters, setFilters] = useState({
    from: "2023-01-01",
    to: new Date().toISOString().split("T")[0],
  });
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const STATUS_ORDER = ["Approved", "Rejected", "Processing"];
  const STATUS_STYLE = isDark
    ? {
        Approved: { color: "text-emerald-100", bg: "bg-emerald-500/10", ring: "border-emerald-400/40" },
        Rejected: { color: "text-rose-100", bg: "bg-rose-500/10", ring: "border-rose-400/40" },
        Processing: { color: "text-amber-100", bg: "bg-amber-500/10", ring: "border-amber-400/40" },
        default: { color: "text-slate-100", bg: "bg-slate-800/60", ring: "border-slate-700" },
      }
    : {
        Approved: { color: "text-emerald-700", bg: "bg-emerald-50", ring: "border-emerald-100" },
        Rejected: { color: "text-rose-700", bg: "bg-rose-50", ring: "border-rose-100" },
        Processing: { color: "text-amber-700", bg: "bg-amber-50", ring: "border-amber-100" },
        default: { color: "text-slate-800", bg: "bg-white", ring: "border-slate-200" },
      };

  const LABEL_COLORS = isDark
    ? {
        Cold: "bg-sky-500/15 text-sky-100 border-sky-400/30",
        Warm: "bg-amber-500/15 text-amber-100 border-amber-400/30",
        Hot: "bg-rose-500/15 text-rose-100 border-rose-400/30",
        NA: "bg-slate-700 text-slate-200 border-slate-600",
      }
    : {
        Cold: "bg-sky-50 text-sky-700 border-sky-100",
        Warm: "bg-amber-50 text-amber-700 border-amber-100",
        Hot: "bg-rose-50 text-rose-700 border-rose-100",
        NA: "bg-slate-100 text-slate-700 border-slate-200",
      };

  async function fetchData(customFilters = filters) {
    if (!fetchUrl) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(fetchUrl, customFilters, {
        headers: { "Content-Type": "application/json" },
        timeout: 20000,
      });
      const json = res.data || {};
      setData({
        statuses: json.statuses || {},
        subStatusCounts: json.subStatusCounts || {},
      });

      if (typeof json.total_cases === "number") {
        setTotalCases(json.total_cases);
      } else {
        const statuses = json.statuses || {};
        const sum = Object.values(statuses).reduce((acc, s) => {
          const v = s && typeof s.total === "number" ? s.total : Number(s?.total) || 0;
          return acc + v;
        }, 0);
        setTotalCases(sum);
      }

      const serverTs = json.last_updated_at ? new Date(json.last_updated_at) : null;
      setLastFetchedAt(serverTs || new Date());
    } catch (err) {
      console.error("Failed to load stats:", err);
      if (err.response) {
        const status = err.response.status;
        const body =
          typeof err.response.data === "string"
            ? err.response.data
            : JSON.stringify(err.response.data).slice(0, 1000);
        setError(`Server error ${status}: ${body}`);
      } else if (err.request) {
        setError("No response from server (network / CORS / backend down).");
      } else {
        setError(err.message || "Unknown error");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const getStatusKey = (name, sourceData) => {
    const statusesObj = sourceData?.statuses || data?.statuses || {};
    const keys = Object.keys(statusesObj || {});
    const found = keys.find((k) => k.toLowerCase() === name.toLowerCase());
    return found || name;
  };

  const StatCard = ({ title: cardTitle, count, labels, style }) => {
    const cardSurface = isDark
      ? "bg-slate-900/80 text-slate-100 shadow-lg backdrop-blur-md"
      : "bg-white text-slate-900 shadow-md";
    const overlay = isDark
      ? "bg-gradient-to-br from-white/5 via-transparent to-white/10"
      : "bg-gradient-to-br from-white/60 via-white/30 to-slate-100/40";
    const skeleton = isDark ? "bg-slate-700" : "bg-slate-200";
    const labelTitle = isDark ? "text-slate-400" : "text-slate-500";
    const labelPillCount = isDark
      ? "bg-slate-900/70 text-slate-100 border border-white/5"
      : "bg-white text-slate-700 border border-slate-200";
    const labelEmpty = isDark ? "text-slate-400" : "text-slate-500";

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`relative overflow-hidden rounded-2xl p-4 md:p-5 border ${style.ring} ${cardSurface} hover:-translate-y-0.5 transition-transform duration-200`}
      >
        <div className={`absolute inset-0 ${style.bg}`} aria-hidden />
        <div className={`absolute inset-0 opacity-40 ${overlay}`} aria-hidden />

        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <div className={`text-[11px] font-semibold uppercase tracking-wide ${style.color}`}>{cardTitle}</div>
              <div className={`mt-1 text-2xl md:text-3xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>
                {loading ? "..." : count}
              </div>
            </div>
            <div className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Total</div>
          </div>

          <div className="mt-3">
            <div className={`text-xs font-medium mb-2 ${labelTitle}`}>Labels</div>
            <div className="flex flex-wrap gap-2">
              {loading ? (
                <>
                  <div className={`h-7 w-16 rounded-full ${skeleton} animate-pulse`} />
                  <div className={`h-7 w-12 rounded-full ${skeleton} animate-pulse`} />
                </>
              ) : !labels || Object.keys(labels).length === 0 ? (
                <div className={`text-xs ${labelEmpty}`}>No labels</div>
              ) : (
                Object.entries(labels).slice(0, 5).map(([label, cnt]) => {
                  const colorClass = LABEL_COLORS[label] || LABEL_COLORS.NA;
                  return (
                    <div
                      key={label}
                      className={`flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs shadow-sm ${colorClass}`}
                    >
                      <div className="font-medium">{label}</div>
                      <div className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${labelPillCount}`}>
                        {cnt}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const SubStatusCard = ({ counts }) => {
    const arr = Object.entries(counts || {}).map(([k, v]) => ({ sub: k, cnt: v }));
    arr.sort((a, b) => b.cnt - a.cnt);
    const subCardSurface = isDark
      ? "border border-indigo-400/30 bg-slate-900/80 shadow-lg backdrop-blur-md"
      : "border border-indigo-100 bg-white shadow-md";
    const subOverlay = isDark
      ? "from-indigo-500/15 via-slate-900/40 to-violet-500/10"
      : "from-indigo-100/60 via-white to-sky-50/60";
    const skeleton = isDark ? "bg-slate-700" : "bg-slate-200";
    const textMuted = isDark ? "text-slate-400" : "text-slate-500";
    const pillBg = isDark
      ? "bg-slate-900/70 text-slate-100 border border-white/5"
      : "bg-slate-100 text-slate-800 border border-slate-200";
    const titleColor = isDark ? "text-indigo-100" : "text-indigo-700";

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`relative overflow-hidden rounded-2xl p-4 md:p-5 ${subCardSurface}`}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${subOverlay}`} aria-hidden />
        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <div className={`text-[11px] font-semibold uppercase tracking-wide ${titleColor}`}>Sub-Status</div>
              <div className={`mt-1 text-2xl font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>
                {loading ? "..." : arr.length}
              </div>
            </div>
            <div className={`text-xs ${textMuted}`}>Types</div>
          </div>

          <div className="mt-3 max-h-56 overflow-y-auto pr-1">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={`h-4 w-28 ${skeleton} rounded animate-pulse`} />
                ))}
              </div>
            ) : arr.length === 0 ? (
              <div className={`text-xs ${textMuted}`}>No sub-status data</div>
            ) : (
              <div className="space-y-2">
                {arr.map(({ sub, cnt }) => (
                  <div key={sub} className={`flex items-center justify-between text-sm ${isDark ? "text-slate-100" : "text-slate-800"}`}>
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{
                          background:
                            sub.toLowerCase() === "null"
                              ? "#cbd5e1"
                              : sub.toLowerCase() === "na"
                              ? "#94a3b8"
                              : sub.toLowerCase().includes("follow")
                              ? "#8b5cf6"
                              : sub.toLowerCase().includes("sell")
                              ? "#fb923c"
                              : sub.toLowerCase().includes("research")
                              ? "#10b981"
                              : "#60a5fa",
                        }}
                      />
                      <div className="text-sm font-medium">{sub}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[12px] ${textMuted}`}>{totalLabel}</span>
                      <div className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pillBg}`}>
                        {cnt}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const Section = ({ title: secTitle, subtitle: secSubtitle, sourceData, isLoading, showBadge }) => {
    const statuses = sourceData?.statuses || {};
    const subStatusCounts = sourceData?.subStatusCounts || {};
    const total = typeof sourceData?.total_cases === "number"
      ? sourceData.total_cases
      : Object.values(statuses).reduce((acc, s) => acc + (s?.total || 0), 0);
    const sectionShell = isDark
      ? "border-slate-800 bg-slate-900/70 text-slate-100 shadow-2xl"
      : "border-slate-200 bg-white text-slate-900 shadow-lg";
    const subtitleTone = isDark ? "text-slate-400" : "text-slate-600";
    const badgeTone = isDark
      ? "bg-amber-500/15 text-amber-100 border border-amber-400/30"
      : "bg-amber-50 text-amber-700 border border-amber-200";
    const totalTone = isDark ? "text-slate-300" : "text-slate-700";
    const totalLabelTone = isDark ? "text-white" : "text-slate-900";
    const totalValueTone = isDark ? "text-slate-100" : "text-slate-800";

    return (
      <div className={`mb-8 rounded-2xl border p-5 md:p-6 backdrop-blur ${sectionShell}`}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold">{secTitle}</h2>
            <div className={`text-sm mt-1 ${subtitleTone}`}>{secSubtitle}</div>
          </div>

          <div className="flex items-center gap-3">
            {showBadge && (
              <span className={`text-xs px-2 py-1 rounded-full ${badgeTone}`}>
                Working - dummy data
              </span>
            )}
            <div className={`text-sm ${totalTone}`}>
              <span className={`font-medium ${totalLabelTone}`}>Total {totalLabel}:</span>{" "}
              <span className={`text-sm font-semibold ${totalValueTone}`}>{isLoading ? "..." : total}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STATUS_ORDER.map((s) => {
            const key = getStatusKey(s, sourceData);
            const bucket = statuses?.[key] || { total: 0, labels: {} };
            const styles = STATUS_STYLE[s] || STATUS_STYLE.default;
            return (
              <StatCard key={`${secTitle}-${s}`} title={s} count={bucket.total || 0} labels={bucket.labels} style={styles} />
            );
          })}

          <SubStatusCard counts={subStatusCounts || {}} />
        </div>
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen ${
        isDark
          ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100"
          : "bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900"
      }`}
    >
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <div
          className={`relative overflow-hidden rounded-3xl border ${
            isDark
              ? "border-slate-800/80 bg-slate-900/75 shadow-[0_30px_90px_rgba(0,0,0,0.45)]"
              : "border-slate-200 bg-white/85 shadow-[0_20px_60px_rgba(15,23,42,0.10)]"
          }`}
        >
          <div
            className={`absolute inset-0 opacity-80 ${
              isDark
                ? "bg-gradient-to-r from-indigo-500/10 via-cyan-500/10 to-transparent"
                : "bg-gradient-to-r from-indigo-100/60 via-cyan-100/50 to-transparent"
            }`}
            aria-hidden
          />

          <div className="relative px-4 py-4 md:px-5 md:py-4 lg:px-6 lg:py-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-500 flex items-center justify-center text-[15px] font-bold text-white shadow-lg shadow-indigo-500/25">
                  {moduleKey}
                </div>

                <div className="leading-tight">
                  <p className="page-kicker">{eyebrow}</p>
                  <h1 className="page-title">{title}</h1>
                  <p className="page-subtitle">{subtitle}</p>
                </div>
              </div>

              <div className="flex items-center justify-between lg:justify-end gap-2">
                <ModeDropdown className="h-8 px-3 rounded-full shadow-sm" />

                <div
                  className={`flex items-center gap-2 rounded-full px-3 py-1 text-[11px] ${
                    isDark
                      ? "border border-slate-700 bg-slate-800/60 text-slate-300"
                      : "border border-slate-200 bg-slate-100 text-slate-700"
                  }`}
                >
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden />
                  <span className="font-semibold">Live</span>
                  <span className={isDark ? "text-slate-400" : "text-slate-500"}>data</span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <div className={`flex flex-wrap items-center gap-3 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                <div
                  className={`flex items-center gap-2 rounded-full px-3 py-1 border ${
                    isDark ? "bg-slate-800/45 border-slate-700/70" : "bg-white border-slate-200"
                  }`}
                >
                  <span className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>Total</span>
                  <span className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {loading ? "..." : totalCases}
                  </span>
                  <span className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>{totalLabel}</span>
                </div>

                <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Last updated:{" "}
                  <span className={isDark ? "text-slate-200" : "text-slate-700"}>
                    {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : "..."}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2">
                <div
                  className={`flex items-center gap-2 rounded-full px-3 py-0.5 border ${
                    isDark ? "bg-slate-800/55 border-slate-700 text-slate-100" : "bg-white border-slate-200 text-slate-800"
                  }`}
                >
                  <CalendarDays className={isDark ? "text-slate-300 w-4 h-4" : "text-slate-500 w-4 h-4"} />

                  <input
                    type="date"
                    value={filters.from}
                    onChange={(e) => handleFilterChange("from", e.target.value)}
                    className={`bg-transparent border-none text-[11px] outline-none p-0 focus:ring-0 w-[112px] ${
                      isDark ? "text-slate-100" : "text-slate-800"
                    }`}
                  />

                  <span className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>—</span>

                  <input
                    type="date"
                    value={filters.to}
                    onChange={(e) => handleFilterChange("to", e.target.value)}
                    className={`bg-transparent border-none text-[11px] outline-none p-0 focus:ring-0 w-[112px] ${
                      isDark ? "text-slate-100" : "text-slate-800"
                    }`}
                  />
                </div>

                <button onClick={handleApplyFilters} className="ui-btn ui-btn-primary">
                  Apply
                </button>

                <button
                  onClick={() => fetchData(filters)}
                  className="ui-btn ui-btn-ghost inline-flex items-center gap-2"
                  aria-label="Refresh"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="text-[11px]">Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            className={`mb-4 p-3 rounded-xl text-sm ${
              isDark
                ? "bg-rose-500/10 border border-rose-500/40 text-rose-100 shadow-lg"
                : "bg-rose-50 border border-rose-200 text-rose-700 shadow-sm"
            }`}
          >
            {error}
          </div>
        )}

        <Section
          title={sectionTitle}
          subtitle={sectionSubtitle}
          sourceData={{ statuses: data?.statuses || {}, subStatusCounts: data?.subStatusCounts || {}, total_cases: totalCases }}
          isLoading={loading}
          showBadge={showWorkingBadge}
        />
      </div>
    </div>
  );
}
